/**
 * Каркас редакторов игровых данных (`/admin/game-data/**`).
 *
 * Здесь живут типы, общие для ВСЕХ таблиц игровых данных: стили персонажа — первая из ~20.
 * Конкретная таблица приносит свои DTO (`ornament.types.ts`) и ОДНУ `MergeSpec` — больше
 * ничего из механики оптимистичной блокировки ей писать не нужно.
 *
 * Разбор каркаса — `docs/game-data.md`.
 */

/**
 * Тело ответа 409 от любого эндпоинта `/admin/game-data/**` (`GameDataConflictErrorDto`).
 * В `current` уже лежит актуальное состояние строки — дополнительный GET делать НЕЛЬЗЯ:
 * за время запроса строка успеет измениться ещё раз.
 */
export interface GameDataConflictDto<TDto = unknown> {
  message?: string;
  current: TDto;
}

/**
 * Сколько строк грузить за запрос в гридах игровых данных. Крупнее, чем в остальной админке
 * (там 12–50 по ширине экрана): таблицу правят целиком, а не листают, и подгрузка мелкими
 * порциями мешала.
 *
 * Потолок задаёт бэк — `size` в Swagger: 1000 (поднят со 100 10.09.2026, сверено со Swagger).
 * Выше лимита бэка не ставить: он ответит 400, и список не загрузится вовсе.
 */
export const GAME_DATA_PAGE_SIZE = 1000;

/**
 * Порция для телефона: там не таблица, а карточки, и они не виртуализированы — тысяча карточек
 * разом тормозила бы так же, как раньше тысяча строк. Правят с телефона редко, подгрузка по
 * прокрутке тут в самый раз.
 */
export const GAME_DATA_MOBILE_PAGE_SIZE = 50;

/** Тело запроса на изменение строки — то, что уходит в PUT. */
export type GameDataPayload = Record<string, unknown>;

/**
 * Фильтры по колонкам списка: query-параметр → значения (docs/game-data.md §2d). У обычного
 * параметра одно значение, у повторяющегося (`ingredients=57:10&ingredients=58`) — несколько.
 * Пустых параметров здесь нет: пустой фильтр не отправляется.
 */
export type ColumnFilterValues = Readonly<Record<string, readonly string[]>>;

/**
 * Расшифровка ссылки на игровую сущность: предмет, NPC, скилл.
 *
 * Приходит ТОЛЬКО на чтение, рядом с голым id (`swapItemId` + `swapItem`). В телах запросов
 * его нет и быть не должно — на запись уходит один id.
 *
 * **Важно:** обогащённые поля нельзя пускать в мёрдж и в тело запроса. Сборщик тела обязан
 * оставить от вложенных списков только те поля, которые бэк принимает, иначе снимок `base`
 * и отправленное `mine` окажутся разной формы и мёрдж увидит правку там, где её не было.
 */
export interface GameDataRefDto {
  id: number;
  /** Локализованное название. `null`, если сущности нет в вики. */
  name?: string | null;
  /**
   * Доп. название предмета из клиента («Реликт», «Ур.6», «MA») — им различаются одноимённые предметы.
   * Локализовано как `name`. `null` у NPC и скиллов, а также если id не нашёлся в вики.
   */
  additionalName?: string | null;
  icon?: string | null;
  /** У NPC не заполняется. */
  iconPanel?: string | null;
}

/** Значение справочника: то, что уходит в тело запроса, и подпись на языке запроса. */
export interface GameDataDictionaryEntryDto {
  id: number;
  title: string;
}

/** Справочники расы, пола и класса персонажа — общие для всех таблиц игровых данных. */
export interface GameDataDictionariesDto {
  races: GameDataDictionaryEntryDto[];
  sexes: GameDataDictionaryEntryDto[];
  classes: GameDataDictionaryEntryDto[];
}

/** Значение поля в терминах мёрджа: скаляр или список объектов. */
export type MergeValue = unknown;

/** Перевод без завязки на дженерики i18next. */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** Одно поле строки в терминах трёхстороннего мёрджа. */
export interface MergeField {
  /** Ключ в теле запроса (`GameDataPayload`). */
  key: string;
  /** i18n-ключ подписи поля — показывается в диалоге разрешения конфликта. */
  labelKey: string;
  /**
   * Сравнение значений. По умолчанию — глубокое сравнение (`deepEquals`),
   * которое считает `null`, `undefined` и пустой массив одним и тем же «пусто».
   */
  compare?: (a: MergeValue, b: MergeValue) => boolean;
  /** Как показать значение человеку. По умолчанию — `formatMergeValue`. */
  format?: (value: MergeValue, t: TranslateFn) => string;
}

/**
 * Описание таблицы для механики оптимистичной блокировки.
 * Это ЕДИНСТВЕННОЕ, что нужно написать новой таблице сверх обычного API-сервиса.
 */
export interface MergeSpec<TDto> {
  /** i18n-ключ названия сущности («Стиль»), для заголовка диалога. */
  entityKey: string;
  /** Поля, участвующие в мёрдже. Поля вне списка переносятся из `mine` как есть. */
  fields: MergeField[];
  /** DTO с сервера → тело запроса на изменение. Мёрдж идёт в терминах тела запроса. */
  project: (dto: TDto) => GameDataPayload;
  /** Версия строки из DTO. */
  version: (dto: TDto) => number;
  /** Подпись конкретной строки в диалоге: «Оружие · стиль 5 · Меч Судьбы». */
  rowLabel?: (dto: TDto) => string;
  /** Кто и когда правил строку последним — показывается рядом с чужими значениями. */
  updatedBy?: (dto: TDto) => { username?: string; at?: string };
}

/** Поле, которое правили с двух сторон по-разному: его разрешает человек. */
export interface FieldConflict {
  key: string;
  labelKey: string;
  /** Значение из формы. */
  mine: MergeValue;
  /** Значение с сервера. */
  theirs: MergeValue;
  format?: (value: MergeValue, t: TranslateFn) => string;
}

/** Результат трёхстороннего мёрджа. */
export interface MergeResult {
  /**
   * Слитое тело запроса. По конфликтным полям пока стоит `mine` —
   * окончательный выбор делает человек в диалоге.
   */
  merged: GameDataPayload;
  /** Пусто → пересохраняем молча. Не пусто → спрашиваем человека. */
  conflicts: FieldConflict[];
}

/** Что перехватчик 409 передаёт в UI, чтобы тот показал диалог. */
export interface ConflictRequest<TDto = unknown> {
  merged: GameDataPayload;
  conflicts: FieldConflict[];
  theirs: TDto;
  spec: MergeSpec<TDto>;
}

/**
 * Мост между axios-перехватчиком и React: перехватчик живёт вне дерева компонентов,
 * а диалог и тосты — внутри. Реализация — `GameDataConflictProvider`.
 */
export interface ConflictHandler {
  /**
   * Показать диалог и дождаться человека.
   * Вернуть слитое тело запроса или `null`, если он отказался сохранять.
   */
  resolve: (request: ConflictRequest) => Promise<GameDataPayload | null>;
  /** Ненавязчиво сообщить, что строку правили параллельно и изменения объединены сами. */
  notifyAutoMerged: (spec: MergeSpec<unknown>) => void;
}

/** Метаданные, которые сервис вешает на запрос, чтобы перехватчик смог слить изменения. */
export interface OptimisticMeta<TDto = any> {
  /** Снимок строки на момент открытия на редактирование, в терминах тела запроса. */
  base: GameDataPayload;
  /** Описание таблицы. */
  spec: MergeSpec<TDto>;
}

/** Ошибка, с которой отклоняется запрос, если человек отказался разрешать конфликт. */
export class ConflictCancelledError extends Error {
  constructor() {
    super('Conflict resolution cancelled');
    this.name = 'ConflictCancelledError';
  }
}

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Снимок строки + спека мёрджа. Ставится сервисом игровых данных,
     * читается ЕДИНСТВЕННЫМ перехватчиком 409 (`conflict-interceptor.ts`).
     */
    optimistic?: OptimisticMeta;
    /**
     * Внутреннее: какой это круг слияния. Больше `MAX_MERGE_ROUNDS` не крутим —
     * иначе два администратора, жмущие «Сохранить» одновременно, зациклят повторы.
     */
    _gameDataMergeRound?: number;
  }
}
